import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import { Button, Icons } from '../../../components/base/BootstrapComponents';
import { addressFormatter, camStatusNumberFormatter, statusNumberFormatter } from '../../../utils/formatters';
import {
  loadListCamera, loadListParkingMeter, loadStatCamera, loadStatParkingMeter,
} from '../actions';
import Loader from '../../../components/base/Loader/Loader';
import ParkingMeterStatCard from './ParkingMeterStatCard';
import CameraMeterStatCard from './CameraMeterStatCard';
import { responseLogWithNotification } from '../../../utils/utils';
import Grid from '../../../components/base/Grid/Grid';
import { StatusFormatterWithProblem } from '../../ParkingMeter/components/ParkingFormatters';
import Tooltip from '../../../components/base/Tooltip/Tooltip';
import { getDashboardData } from '../reducer';
import { hostCommandDispatch } from '../../ParkingMeter/actions';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { PARKING_METER_COMMAND } from '../../ParkingMeter/constants';
import { getParkingMetersData } from '../../ParkingMeter/reducer';
import PermissionAccessWrapper from '../../../components/base/PermissionAccessWrapper';
import { showGlobalConfirm } from '../../System/actions';

export default function ComponentsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dashboardModule = useSelector(getDashboardData);
  const {
    loadingStatParkingMeter,
    statParkingMeter,
    loadingStatCamera,
    statCamera,
    loadingListParkingMeter,
    listParkingMeter,
    loadingListCamera,
    listCamera,
  } = dashboardModule;
  const {
    loadingHostCommandDispatch,
  } = useSelector(getParkingMetersData);
  const intl = useIntl();
  const { formatMessage } = intl;
  const handleLoadListParkingMeter = () => {
    dispatch(loadListParkingMeter()).catch(responseLogWithNotification);
  };

  const handleLoadListCamera = () => {
    dispatch(loadListCamera()).catch(responseLogWithNotification);
  };

  const handleReload = id => () => {
    dispatch(hostCommandDispatch({ id, command: PARKING_METER_COMMAND.DEVICE_REBOOT })).then(() => {
      notificationDecorator({
        title: 'Создание команды',
        message: formatMessage({ id: 'success' }),
      });
    }).catch(response => {
      responseLogWithNotification(response, 'Создание команды');
    });
  };

  const handleReloadCameras = () => {
    if (!listCamera || !listCamera.length) {
      return;
    }
    dispatch(hostCommandDispatch({ id: listCamera.map(c => c.id), command: PARKING_METER_COMMAND.DEVICE_REBOOT })).then(data => {
      notificationDecorator({
        title: 'Создание команды',
        message: formatMessage({ id: 'success' }),
      });
      const { commands } = data.result;
      const keys = Object.keys(commands);
      dispatch(showGlobalConfirm({
        confirmTitle: 'Создание команды Перезагрузка устройства',
        confirmMessage: keys.map(key => <div key={key}>{key}: {commands[key].message}</div>),
        confirmCancelButtonText: 'Закрыть',
        hideSubmit: true,
      }));
    }).catch(response => {
      responseLogWithNotification(response, 'Создание команды');
    });
  };

  const handleReloadPM = () => {
    if (!listParkingMeter || !listParkingMeter.length) {
      return;
    }
    dispatch(hostCommandDispatch({ id: listParkingMeter.map(c => c.id), command: PARKING_METER_COMMAND.DEVICE_REBOOT })).then(data => {
      notificationDecorator({
        title: 'Создание команды',
        message: formatMessage({ id: 'success' }),
      });
      const { commands } = data.result;
      const keys = Object.keys(commands);
      dispatch(showGlobalConfirm({
        confirmTitle: 'Создание команды Перезагрузка устройства',
        confirmMessage: keys.map(key => <div key={key}>id: {key}: {commands[key].message}</div>),
        confirmCancelButtonText: 'Закрыть',
        hideSubmit: true,
      }));
    }).catch(response => {
      responseLogWithNotification(response, 'Создание команды');
    });
  };

  useEffect(() => {
    dispatch(loadStatParkingMeter()).catch(responseLogWithNotification);
    dispatch(loadStatCamera()).catch(responseLogWithNotification);
    handleLoadListParkingMeter();
    handleLoadListCamera();
  }, []);

  const reloadFormatter = id => (
    <PermissionAccessWrapper requiredPermissions={['execute_device_reboot_command']} locked>
      <Button className="parking-meter-reload">
        <Icons width={32} height={32} customIcon icon="replay" className="parking-meter-reload-icon" onClick={handleReload(id)} />
      </Button>
    </PermissionAccessWrapper>
  );

  const dashboardHelper = () => (
    <div className="park-meter-status-block">
      <div className="park-meter-legend">
        Легенда
      </div>
      <div className="park-meter-status good-status">
        <div className="park-meter-status-color" />
        <div className="park-meter-status-text">Доступен</div>
      </div>
      <div className="park-meter-status warning-status">
        <div className="park-meter-status-color" />
        <div className="park-meter-status-text">Временно недоступен</div>
      </div>
      <div className="park-meter-status error-status">
        <div className="park-meter-status-color" />
        <div className="park-meter-status-text">Недоступен</div>
      </div>
      <div className="park-meter-status">
        <div className="park-meter-status-icon">
          <Icons width={24} height={24} customIcon icon="replay" className="parking-meter-reload" />
        </div>
        <div className="park-meter-status-text">Перезагрузка компьютера</div>
      </div>
    </div>
  );

  const columns = [
    {
      dataField: 'host',
      text: 'Номер',
      formatter: statusNumberFormatter,
      disableSort: true,
      dataClassName: 'align-middle',
      headerStyle: { width: '10%' },
    },
    {
      dataField: 'zbx_address',
      text: 'Адрес',
      formatter: zbx_address => addressFormatter(zbx_address, true),
      disableSort: true,
      dataClassName: 'align-middle',
      headerStyle: { width: '32%' },
    },
    {
      dataField: 'status',
      text: 'Состояние',
      formatter: (_, item) => <StatusFormatterWithProblem item={item} />,
      disableSort: true,
      dataClassName: 'align-middle',
      headerStyle: { width: '18%' },
    },
    {
      dataField: 'last_unsolved_problem',
      text: 'Ошибка',
      disableSort: true,
      dataClassName: 'align-middle',
      formatter: i => <span style={{ lineHeight: '140%' }}>{i.message}</span>,
      headerStyle: { width: '34%' },
    },
    {
      dataField: 'id',
      text: '',
      formatter: reloadFormatter,
      disableSort: true,
      headerStyle: { width: '6%' },
      dataClassName: 'reload-wrapper',
    },
  ];

  const camColumns = [
    {
      dataField: 'host',
      text: 'Номер',
      formatter: camStatusNumberFormatter,
      disableSort: true,
      headerStyle: { width: '10%' },
      dataClassName: 'align-middle',
    },
    {
      dataField: 'zbx_address',
      text: 'Адрес',
      formatter: zbx_address => addressFormatter(zbx_address, true),
      disableSort: true,
      headerStyle: { width: '32%' },
      dataClassName: 'align-middle',
    },
    {
      dataField: 'status',
      text: 'Состояние',
      formatter: (_, item) => <StatusFormatterWithProblem item={item} />,
      disableSort: true,
      headerStyle: { width: '18%' },
      dataClassName: 'align-middle',
    },
    {
      dataField: 'last_unsolved_problem',
      text: 'Ошибка',
      disableSort: true,
      formatter: i => <span style={{ lineHeight: '140%' }}>{i.message}</span>,
      headerStyle: { width: '34%' },
      dataClassName: 'align-middle',
    },
    {
      dataField: 'id',
      text: '',
      formatter: reloadFormatter,
      disableSort: true,
      headerStyle: { width: '6%' },
      dataClassName: 'reload-wrapper',
    },
  ];

  return (
    <div className="content g-0 dashboard-page">
      <Loader loading={loadingHostCommandDispatch || loadingStatParkingMeter || loadingStatCamera || loadingListParkingMeter || loadingListCamera} />
      <PageHeader header="Рабочий стол" />
      <div className="dashboard-page-content g-0">
        <div className="dashboard-page-row">
          <div className="dashboard-chart-column">
            <ParkingMeterStatCard data={statParkingMeter} />
          </div>
          <div className="dashboard-chart-column">
            <CameraMeterStatCard data={statCamera} />
          </div>
        </div>
        <div className="dashboard-page-row dashboard-tables-row">
          <div className="dashboard-table-column">
            <div className="frame dashboard-tables-block">
              <div className="dashboard-tables-header">
                <div className="dashboard-tables-header-title">Паркоматы, требующие внимания</div>
                <span className="dashboard-show-all small" onClick={() => { navigate('/parking-meters'); }}>Показать всё</span>
              </div>
              <div className="dashboard-tables-filter">
                <div className="camera-helper">
                  <Tooltip
                    tooltipContent={dashboardHelper()}
                    content={<Icons width={20} height={20} customIcon icon="help-outline" className="camera-helper-icons" />}
                  />
                </div>
                <div className="dashboard-reload small">
                  <PermissionAccessWrapper requiredPermissions={['execute_device_reboot_command']} locked>
                    <Button variant="primary" className="small" onClick={handleReloadPM}>Перезагрузить всё</Button>
                  </PermissionAccessWrapper>
                </div>
              </div>
              <div className="dashboard-tables-wrapper">
                <div className="sticky-table-wrapper custom-scroll-theme">
                  <Grid
                    loading={loadingListParkingMeter}
                    loadList={() => {}}
                    loadingList={false}
                    columns={columns}
                    data={listParkingMeter}
                    className="list row-border"
                    hidePagination
                    emptyText="Отсутствуют паркоматы с техническими проблемами"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="dashboard-table-column">
            <div className="frame  dashboard-tables-block">
              <div className="dashboard-tables-header">
                <div className="dashboard-tables-header-title">Камеры видеонаблюдения, требующие внимания</div>
                <span className="dashboard-show-all small" onClick={() => { navigate('/cameras'); }}>Показать всё</span>
              </div>
              <div className="dashboard-tables-filter">
                <div className="camera-helper">
                  <Tooltip
                    tooltipContent={dashboardHelper()}
                    content={<Icons width={20} height={20} customIcon icon="help-outline" className="camera-helper-icons" />}
                  />
                </div>
                <div className="dashboard-reload small">
                  <PermissionAccessWrapper requiredPermissions={['execute_device_reboot_command']} locked>
                    <Button variant="primary" className="small" onClick={handleReloadCameras}>Перезагрузить всё</Button>
                  </PermissionAccessWrapper>
                </div>
              </div>
              <div className="dashboard-tables-wrapper">
                <div className="sticky-table-wrapper custom-scroll-theme">
                  <Grid
                    loading={loadingListCamera}
                    loadList={() => {}}
                    loadingList={false}
                    columns={camColumns}
                    data={listCamera}
                    className="list row-border"
                    hidePagination
                    emptyText="Отсутствуют видеокамеры с техническими проблемами"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
